import { useCallback, useEffect, useRef, useState } from 'react';

import { supabase } from '../../../lib/supabase';
import { getCurrentUserId } from '../../../utils/authUtils';
import type { Job } from '../components/JobCard';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type SavedJobsHook = {
  /** Composite key set: `${source}:${external_id}` */
  savedKeys: Set<string>;
  toggle: (job: Job) => Promise<void>;
  /** Transient error message; set to null with clearError() after display */
  errorMessage: string | null;
  clearError: () => void;
};

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Manages the saved-jobs bookmark state for the current user.
 *
 * Reads from public.saved_jobs (RLS limits rows to auth.uid() automatically).
 * Performs optimistic add / remove, rolling back and surfacing an error
 * message when the DB write fails.
 *
 * Pass `isSignedIn` from the parent so the hook can react to auth transitions
 * without running its own auth check on every render.
 */
export function useSavedJobs(isSignedIn: boolean): SavedJobsHook {
  const [savedKeys, setSavedKeys] = useState<Set<string>>(new Set());
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Stable ref so toggle() always sees current savedKeys without needing it
  // in the useCallback dependency array (avoids re-creating toggle on every
  // bookmark toggle, which would otherwise cancel in-flight optimistic state).
  const savedKeysRef = useRef(savedKeys);
  useEffect(() => {
    savedKeysRef.current = savedKeys;
  }, [savedKeys]);

  const clearError = useCallback(() => setErrorMessage(null), []);

  // Load on mount and whenever sign-in state changes
  useEffect(() => {
    if (!isSignedIn) {
      setSavedKeys(new Set());
      return;
    }

    supabase
      .from('saved_jobs')
      .select('source, external_id')
      .limit(500)
      .then(({ data }) => {
        if (data) {
          setSavedKeys(
            new Set(data.map(r => `${r.source as string}:${r.external_id as string}`)),
          );
        }
      });
  }, [isSignedIn]);

  const toggle = useCallback(
    async (job: Job) => {
      if (!isSignedIn) return;
      const userId = await getCurrentUserId();
      if (!userId) return;

      const key = `${job.source}:${job.external_job_id ?? job.id}`;
      const wasSaved = savedKeysRef.current.has(key);

      // Optimistic update
      setSavedKeys(prev => {
        const next = new Set(prev);
        if (wasSaved) next.delete(key);
        else next.add(key);
        return next;
      });

      try {
        if (wasSaved) {
          const { error } = await supabase
            .from('saved_jobs')
            .delete()
            .eq('user_id', userId)
            .eq('source', job.source)
            .eq('external_id', job.external_job_id ?? job.id);
          if (error) throw error;
        } else {
          const { error } = await supabase.from('saved_jobs').upsert({
            user_id:     userId,
            source:      job.source,
            external_id: job.external_job_id ?? job.id,
            url:         job.url,
            title:       job.title,
            company:     job.company,
          });
          if (error) throw error;
        }
      } catch {
        // Rollback the optimistic change
        setSavedKeys(prev => {
          const next = new Set(prev);
          if (wasSaved) next.add(key);
          else next.delete(key);
          return next;
        });
        setErrorMessage('Could not update saved jobs');
      }
    },
    [isSignedIn],
  );

  return { savedKeys, toggle, errorMessage, clearError };
}
